import React, {ChangeEvent, FC, useEffect, useRef, useState} from 'react';
import Api from "../../../../api";
import FullscreenModal from "components/FullscreenModal";
import styles from "./components.module.scss";
import MarkdownEditor from "../../../../components/MarkdownEditor/MarkdownEditor";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import clsx from "clsx";
import {SimpleModal} from "components/Modal";
import {RemoveContactInfoModal} from "../../../landing/SignUp2_Talent/components/RemoveContactinfoModal";
import {ChooseBio} from "../../../landing/SignUp2_Talent/components/ChooseBio";
import FixedLoader from "components/FixedLoader/FixedLoader";
import { WritingAssistFlow } from "components/WritingAssist";
import { isWritingAssistV2Enabled } from "utils/writingAssistV2";


export interface CoverLetterProps {
    coverLetter?: string,
    name?: string,
    isOpen: boolean,
    onClose: () => void,
    onSaveCoverLetter: (coverLetter: string) => void
}

interface CheckTextResponse {
    spellChecked: string;
    enhanced: string;
    error?: boolean; 
    message?: string;
}

const MIN_WORDS = 10;

const countWords = (inputString: string): number => {
    const trimmed = inputString.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).length;
}

const tryLaterText = "Sorry, you've used all of your profile assists for today. Please skip or try again tomorrow."

const LegacyCoverLetter: FC<CoverLetterProps> = (
    {
        coverLetter,
        name,
        isOpen,
        onClose,
        onSaveCoverLetter
    }) => {
    const [inputValue, setInputValue] = useState(coverLetter || '');
    const [spellCheckedResponse, setSpellCheckedResponse] = useState('');
    const [enhancedResponse, setEnhancedResponse] = useState('');
    const [removeContactInfoModal, setRemoveContactInfoModal] = useState(false);
    const [isChooseBioOpen, setIsChooseBioOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [aboutError, setAboutError] = useState<string>();
    const [rewritesLeft, setRewritesLeft] = useState<number>(5);
    const [rewritesLeftError, setRewritesLeftError] = useState<string>("");

    const getRewritesLeft = async () => {
        try {
            const response = await Api.getAiAssistsLeft();
            if (response.aiAssistsLeft && typeof(response.aiAssistsLeft === 'number')) {
                setRewritesLeft(response.aiAssistsLeft);
                return response.aiAssistsLeft;
            } else {
                console.error('Invalid response format:', response);
                setRewritesLeftError(tryLaterText);
                setRewritesLeft(0);
                return 0;
            }
        } catch (error) {
            console.error('Error fetching rewrites left:', error);
            setRewritesLeftError(tryLaterText);
            setRewritesLeft(0);
            return 0;
        }
    };

    const onSubmit = async (event: any) => {
        event.preventDefault();
        getRewritesLeft();
        if(rewritesLeft > 0){
            if (countWords(inputValue) < MIN_WORDS) {
                setAboutError('Please write a bit more.');
                return;
            }
            setAboutError(undefined);
            try {
                setLoading(true);
                const message: CheckTextResponse = await Api.checkTextContactInfo(inputValue, 'coverLetter');
                if (message?.error) {
                    setAboutError(message?.message || 'An error occurred. Please try again later.');
                } else {
                    setSpellCheckedResponse(message.spellChecked);
                    setEnhancedResponse(message.enhanced);
                    setIsChooseBioOpen(true);
                    setRewritesLeft(rewritesLeft - 1);
                }
            } catch (e: any) {
                console.error('There was an error!', e);
                let errorMsg = 'An unexpected error occurred. Please try again later.';
                try {
                    const parsed = typeof e?.message === 'string' ? JSON.parse(e.message) : null;
                    if (parsed?.message) errorMsg = parsed.message;
                } catch { /* keep generic message */ }
                setAboutError(errorMsg);
            } finally {
                setLoading(false);
            }
        } else {
            setRewritesLeft(0);
            setRewritesLeftError(tryLaterText);
            console.error(rewritesLeftError);
        }
    };

    useEffect(() => {
        setInputValue(coverLetter || '');
    }, [coverLetter]);

    useEffect(() => {
        getRewritesLeft();
    }, []);

    const handleInputChange = (value: string) => {
        setInputValue(value);
    }

    return (
        <>
            {isChooseBioOpen ? <ChooseBio
                isOpen={isChooseBioOpen}
                onBack={() => setIsChooseBioOpen(false)}
                spellCheckedResponse={spellCheckedResponse}
                enhancedResponse={enhancedResponse}
                onSave={(text: string) => {
                    onSaveCoverLetter(text);
                    onClose();
                }}
                removeContactInfoModal={removeContactInfoModal}
                setRemoveContactInfoModal={setRemoveContactInfoModal}
                rewritesLeft={rewritesLeft}
                isCoverLetter={true}
            /> : null}
            <FullscreenModal
                title={'Base Cover Letter'}
                isOpen={isOpen && !isChooseBioOpen}
                buttonText={'Review & Save'}
                onClose={onClose}
                onSubmit={onSubmit}
                stickyBottom={false}
                disabledSubmit={countWords(inputValue) < MIN_WORDS}
            >
                <SimpleModal isOpen={removeContactInfoModal}>
                    <RemoveContactInfoModal onCloseButtonClick={() => setRemoveContactInfoModal(false)}/>
                </SimpleModal>

                <div className={styles.minimalContainer}>
                    {/* Title Section */}
                    <div className={styles.titleSection}>
                        <h1 className={styles.mainTitle}>Base Cover Letter</h1>
                        <p className={styles.greetingText}>Hey {name}! 👋</p>
                    </div>

                    {/* Instructions Card */}
                    <div className={styles.instructionsCard}>
                        <h2>Write a compelling cover letter</h2>
                        <p>Write in clear paragraphs. Line breaks and indentation are preserved.</p>
                    </div>

                    {/* Editor Section with inline header */}
                    <div className={styles.editorSectionMinimal}>
                        <div className={styles.editorCardMinimal}>
                            <div className={styles.editorInlineHeader}>
                                <div className={styles.leftSection}>
                                    <span className={styles.labelText}>Your Cover Letter</span>
                                    <div className={styles.formatBadge}>
                                        Format your cover letter for greater impact
                                    </div>
                                </div>
                                {/* Toolbar will be positioned here by CSS */}
                            </div>
                            
                            <div className={styles.editorContent}>
                                <MarkdownEditor
                                    value={inputValue}
                                    onChange={handleInputChange}
                                    placeholder="Introduce yourself and highlight your skills..."
                                    label=""
                                    error={!!aboutError}
                                    helperText={aboutError}
                                    rows={14}
                                    className={styles.minimalMarkdownEditor}
                                    mode={'plain'}
                                />
                                {(() => {
                                    const words = countWords(inputValue);
                                    const metMin = words >= MIN_WORDS;
                                    return (
                                        <div
                                            className={clsx(
                                                styles.wordCountFooter,
                                                metMin ? styles.wordCountMet : styles.wordCountBelow
                                            )}
                                            aria-live="polite"
                                        >
                                            {metMin
                                                ? `${words} words`
                                                : `${words} / ${MIN_WORDS} min words`}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                        
                        {rewritesLeftError && (
                            <div className={styles.errorAlert}>
                                {rewritesLeftError}
                            </div>
                        )}
                    </div>

                    {/* Privacy Note */}
                    <div className={styles.privacyNoteMinimal}>
                        <p>
                            <strong>Note:</strong> Don't include contact information (email, phone, etc.) in this section. It gets removed automatically.
                        </p>
                    </div>

                    {/* Help Section */}
                    <div className={styles.helpSectionMinimal}>
                        <div className={styles.helpIcon}>
                            <InfoOutlinedIcon />
                        </div>
                        <div className={styles.helpTextContent}>
                            <h3>Need help writing?</h3>
                            <p>
                                When you hit "Review & Save" we'll fix spelling & punctuation and show an optional refined version. Output stays plain text with paragraphs.
                            </p>
                            {rewritesLeft > 0 && (
                                <p className={styles.assistsText}>
                                    {rewritesLeft} AI assist{rewritesLeft !== 1 ? 's' : ''} remaining today
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </FullscreenModal>
            <FixedLoader
                loading={loading}
            />
        </>
    );
}

const WritingAssistCoverLetter: FC<CoverLetterProps> = ({
    coverLetter,
    isOpen,
    onClose,
    onSaveCoverLetter,
}) => (
    <FullscreenModal
        title="Base Cover Letter"
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={(event) => event.preventDefault()}
        stickyBottom={false}
    >
        <div data-writing-assist-version="v2">
            <WritingAssistFlow
                context="baseCoverLetter"
                initialText={coverLetter || ''}
                save={async ({ text }) => {
                    const result = await Api.updateHygienistAccount({ baseCoverLetter: text });
                    onSaveCoverLetter(text);
                    return result;
                }}
                onSaved={onClose}
            />
        </div>
    </FullscreenModal>
);

export const CoverLetter: FC<CoverLetterProps> = (props) => (
    isWritingAssistV2Enabled()
        ? <WritingAssistCoverLetter {...props} />
        : <LegacyCoverLetter {...props} />
);
